import Drawer from 'src/components/ui/drawer/Drawer';
import { formatDate } from 'src/utils/formatDate';
import { formatMoney } from 'src/utils/formatMoney';
import { formatPhoneNumber } from 'src/utils/formatPhoneNumber';
import { IPortfolioManagerDetails } from 'src/services/models/portfolio.service';
import { NUMBER_ZERO } from 'src/utils/constants';
import { PortfolioService } from 'src/services/portfolio.service';
import Spinner from 'src/components/ui/spinner/index';
import { useLocalization } from 'src/utils/useLocalization';
import React, { useEffect, useState } from 'react';
import './styles.scss';

interface IPortfolioManagerDetailProps {
    dealerId: string;
    accountNumber: string;
    country: string;
    setError: VoidFunction;
    close: VoidFunction;
}

interface IPortfolioManagerDetailsProps {
    details: IPortfolioManagerDetails;
}

interface IPortfolioManagerDetailsItem {
    title: string;
    rows: Array<{
        rowName: string;
        rowValue: string;
        show?: boolean;
    }> | null;
}

type QuoteType = 'customer' | 'dealer';
type BoughtAsLabel = 'n' | 'u' | 'd' | 'demo' | 'c' | 'cpo';
type PaymentFrequency =
    | 'monthly'
    | 'biweekly'
    | 'semi-monthly'
    | 'single pay'
    | 'weekly';

const loader = (
    <div className='loader_container' data-testid='loader'>
        <Spinner />
    </div>
);

export const isNullOrUndefined = (value: any): boolean => {
    return value === null || value === undefined || value === '';
};

const PortfolioManagerDetailsItem: React.FC<IPortfolioManagerDetailsItem> = ({
    title,
    rows,
}) => {
    if (!rows) return <></>;

    return (
        <div className='portfolio-detail-item u-mt' key={title}>
            <div className='portfolio-detail-item-title'>
                <h5>{title}</h5>
            </div>
            {rows.map((item, index) => {
                if (item.rowName === '' || !item.show) return null;
                return (
                    <React.Fragment key={`${title}-${index}`}>
                        <div className='portfolio-detail-item-label'>
                            {item.rowName}
                        </div>
                        <div className='portfolio-detail-item-value'>
                            {item.rowValue}
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const PortfolioManagerDetails: React.FC<IPortfolioManagerDetailsProps> = ({
    details,
}) => {
    const isUSA = details.country === 'USA';
    const isLeaseOrBallonAccount = ['lease', 'balloon'].includes(
        details?.accountType?.toLowerCase() || '',
    );
    const isLoanOrBallonAccount = ['loan', 'balloon'].includes(
        details?.accountType?.toLowerCase() || '',
    );
    const isLeaseAccount = details?.accountType?.toLowerCase() === 'lease';
    const isLoanAccount = details?.accountType?.toLowerCase() === 'loan';
    const { portfolioManager, portfolioManagerDetails } = useLocalization();

    function getProgramEligibility() {
        const { programEligibility } = details || {};
        return Array.isArray(programEligibility) &&
            programEligibility.length > NUMBER_ZERO
            ? programEligibility.join(' | ')
            : portfolioManager.PORTFOLIO_NONE_LABEL;
    }

    const getBuyerDetails = (role: 'Primary Buyer' | 'Co Buyer') => {
        const party = details?.parties?.find(
            (party) => party.PartyRoleType === role,
        );

        return {
            homePhone:
                party?.PhoneNumbers?.find((phone) => phone.TypeCode === 'home')
                    ?.PhoneNumber || '',
            workPhone:
                party?.PhoneNumbers?.find((phone) => phone.TypeCode === 'work')
                    ?.PhoneNumber || '',
            cellPhone:
                party?.PhoneNumbers?.find(
                    (phone) => phone.TypeCode === 'mobile',
                )?.PhoneNumber || '',
            email: party?.EmailAddress || '',
            garagingAddress: formatAddress(
                party?.Addresses?.find(
                    (address) => address.TypeCode === 'garaging',
                ) || {},
            ),
        };
    };

    function formatAddress(address: any) {
        const addressParts = [
            address.AddressLine1,
            address.AddressLine2,
            address.City,
            address.StateOrProvince,
            address.PostalCode,
            address.Country,
        ];
        return addressParts.filter((part) => part).join(', ') || '';
    }

    const buyerDetails = getBuyerDetails('Primary Buyer');
    const cobuyerDetails = getBuyerDetails('Co Buyer');
    const vehicleDetails = details?.vehicle || {};

    const getRows = (
        fields: Array<{
            rowName: string;
            value: any;
            isDate?: boolean;
            show?: boolean;
        }>,
    ) => {
        return fields.map(({ rowName, value, isDate, show = true }) => {
            return {
                rowName,
                rowValue: isNullOrUndefined(value)
                    ? ''
                    : isDate
                    ? formatDate(value) || ''
                    : String(value),
                show,
            };
        });
    };

    const customerInformationRows = getRows([
        {
            rowName: portfolioManagerDetails.CUSTOMER_INFORMATION_BUYER,
            value:
                details?.parties?.find(
                    (party) => party.PartyRoleType === 'Primary Buyer',
                )?.FullName || '',
        },
        {
            rowName: portfolioManagerDetails.CUSTOMER_INFORMATION_CO_BUYER,
            value:
                details?.parties?.find(
                    (party) => party.PartyRoleType === 'Co Buyer',
                )?.FullName || '',
        },
        {
            rowName:
                portfolioManagerDetails.CUSTOMER_INFORMATION_CURRENT_MATURITY_DATE,
            value: details.currentMaturityDate,
            isDate: true,
        },
        {
            rowName:
                portfolioManagerDetails.CUSTOMER_INFORMATION_ORIGINAL_MATURITY_DATE,
            value: details.originalMaturityDate,
            isDate: true,
        },
        {
            rowName: portfolioManagerDetails.CUSTOMER_INFORMATION_VIN,
            value: details.vin,
        },
    ]);

    const customerContactInformationRows = getRows([
        {
            rowName:
                portfolioManagerDetails.CONTACT_INFORMATION_GARAGING_ADDRESS,
            value: buyerDetails.garagingAddress,
        },
        {
            rowName: portfolioManagerDetails.CONTACT_INFORMATION_HOME_PHONE,
            value: formatPhoneNumber(buyerDetails.homePhone),
        },
        {
            rowName: portfolioManagerDetails.CONTACT_INFORMATION_WORK_PHONE,
            value: formatPhoneNumber(buyerDetails.workPhone),
        },
        {
            rowName: portfolioManagerDetails.CONTACT_INFORMATION_CELL_PHONE,
            value: formatPhoneNumber(buyerDetails.cellPhone),
        },
        {
            rowName: portfolioManagerDetails.CONTACT_INFORMATION_EMAIL,
            value: buyerDetails.email,
        },
        {
            rowName:
                portfolioManagerDetails.CONTACT_INFORMATION_CO_BUYER_ADDRESS,
            value: cobuyerDetails.garagingAddress,
        },
        {
            rowName: portfolioManagerDetails.CONTACT_INFORMATION_CO_BUYER_PHONE,
            value: formatPhoneNumber(cobuyerDetails.homePhone),
        },
        {
            rowName:
                portfolioManagerDetails.CONTACT_INFORMATION_CO_BUYER_WORK_PHONE,
            value: formatPhoneNumber(cobuyerDetails.workPhone),
        },
        {
            rowName:
                portfolioManagerDetails.CONTACT_INFORMATION_CO_BUYER_CELL_PHONE,
            value: formatPhoneNumber(cobuyerDetails.cellPhone),
        },
        {
            rowName: portfolioManagerDetails.CONTACT_INFORMATION_CO_BUYER_EMAIL,
            value: cobuyerDetails.email,
        },
    ]);

    const vehicleInformationRows = getRows([
        {
            rowName: portfolioManagerDetails.VEHICLE_INFORMATION_YEAR,
            value: vehicleDetails.year,
        },
        {
            rowName: portfolioManagerDetails.VEHICLE_INFORMATION_MAKE,
            value: vehicleDetails.make,
        },
        {
            rowName: portfolioManagerDetails.VEHICLE_INFORMATION_MODEL,
            value: vehicleDetails.model,
        },
        {
            rowName: portfolioManagerDetails.VEHICLE_INFORMATION_MODEL_CODE,
            value: vehicleDetails.modelCode,
        },
        {
            rowName: portfolioManagerDetails.VEHICLE_INFORMATION_TERM,
            value: details.contractTerm,
        },
        {
            rowName: portfolioManagerDetails.VEHICLE_INFORMATION_COLOR,
            value: vehicleDetails.vehicleColor,
        },
        {
            rowName: portfolioManagerDetails.VEHICLE_INFORMATION_DESCRIPTION,
            value: vehicleDetails.description,
        },
        {
            rowName: portfolioManagerDetails.VEHICLE_INFORMATION_OPTIONS,
            value:
                vehicleDetails?.options?.length === NUMBER_ZERO
                    ? portfolioManager.PORTFOLIO_NONE_LABEL
                    : vehicleDetails?.options?.join(', ') || '',
        },
    ]);

    const quoteType: Record<QuoteType, string> = {
        customer:
            portfolioManagerDetails.ACCOUNT_INFORMATION_QUOTE_TYPE_VALUES_CUSTOMER,
        dealer: portfolioManagerDetails.ACCOUNT_INFORMATION_QUOTE_TYPE_VALUES_DEALER,
    };

    const getQuoteTypeLabel = (quoteTypeValue: string): string => {
        const lowercasedQuoteType = quoteTypeValue.toLowerCase() as QuoteType;

        if (quoteType[lowercasedQuoteType]) {
            return quoteType[lowercasedQuoteType];
        }

        return '';
    };

    const boughtAsLabel: Record<BoughtAsLabel, string> = {
        n: portfolioManagerDetails.ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_NEW,
        u: portfolioManagerDetails.ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_PREOWNED,
        d: portfolioManagerDetails.ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_DEMO,
        demo: portfolioManagerDetails.ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_DEMO,
        c: portfolioManagerDetails.ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_CPO,
        cpo: portfolioManagerDetails.ACCOUNT_INFORMATION_BOUGHT_AS_TYPE_CPO,
    };

    const getBoughtAsLabel = (boughtAsValue: string): string => {
        const lowercaseBoughtAsValue =
            boughtAsValue.toLowerCase() as BoughtAsLabel;

        if (boughtAsLabel[lowercaseBoughtAsValue]) {
            return boughtAsLabel[lowercaseBoughtAsValue];
        }

        return '';
    };

    const paymentFrequency: Record<PaymentFrequency, string> = {
        monthly:
            portfolioManagerDetails.ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_MONTHLY,
        biweekly:
            portfolioManagerDetails.ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_BIWEEKLY,
        'semi-monthly':
            portfolioManagerDetails.ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_SEMI_MONTHLY,
        'single pay':
            portfolioManagerDetails.ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_SINGLE_PAY,
        weekly: portfolioManagerDetails.ACCOUNT_INFORMATION_PAYMENT_FREQUENCY_VALUES_WEEKLY,
    };

    const getPaymentFrequencyLabel = (frequency: string): string => {
        const lowercasedFrequency = frequency.toLowerCase() as PaymentFrequency;

        if (paymentFrequency[lowercasedFrequency]) {
            return paymentFrequency[lowercasedFrequency];
        }

        return '';
    };

    const accountInformationRows = getRows([
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_ACCOUNT_NUMBER,
            value: details.accountNumber,
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_CONTRACT_DATE,
            value: details.contractDate,
            isDate: true,
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_BRAND_SP_CODE,
            value: details.brandSpCode,
            show: isUSA,
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_ACCOUNT_TYPE,
            value: details.accountType,
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_TOTAL_ACCOUNTS,
            value: isUSA
                ? details.totalAccounts
                : details.totalAccounts ?? NUMBER_ZERO,
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_BOUGHT_AS,
            value: details.boughtAs ? getBoughtAsLabel(details.boughtAs) : '',
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_ORIGINAL_ODOMETER,
            value: details.originalOdometer,
            show: isLeaseOrBallonAccount,
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_ALLOWABLE_MILEAGE,
            value: details.allowedMileage,
            show: isLeaseOrBallonAccount,
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_CURRENT_RESIDUAL,
            value: formatMoney(details.currentResidual),
            show: isLeaseAccount,
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_SECURITY_DEPOSIT,
            value: formatMoney(details.securityDeposit),
            show: isLeaseAccount,
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_BASE_PAYMENT,
            value: formatMoney(details.basePayment),
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_PAYMENT_FREQUENCY,
            value: details.paymentFrequency
                ? getPaymentFrequencyLabel(details.paymentFrequency)
                : '',
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_MONEY_FACTOR,
            value: formatMoney(details.moneyFactor),
            show: isLeaseAccount,
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_DOWN_PAYMENT,
            value: formatMoney(details.downPayment),
            show: isLoanOrBallonAccount,
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_APR,
            value: details.apr + '%',
            show: isLoanOrBallonAccount,
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_OUTSTANDING_BALANCE,
            value: formatMoney(details.outstandingBalance),
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_TURN_IN_FEE,
            value: details.turnInFee,
            show: isUSA,
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_PROGRAM_ELIGIBILITY,
            value: getProgramEligibility(),
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_ESTIMATED_BB_VALUE,
            value: formatMoney(details.blackEstimatedMarketValue),
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_ESTIMATED_EQUITY,
            value: formatMoney(details.blackEquity),
            show: isLoanAccount,
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_DOWN_PAYMENT,
            value: formatMoney(details.downPayment),
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_LAST_QUOTE_GENERATED,
            value: details.lastQuoteGeneratedDt,
            isDate: true,
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_QUOTE_TYPE,
            value: details.quoteType
                ? getQuoteTypeLabel(details.quoteType)
                : '',
        },
        {
            rowName:
                portfolioManagerDetails.ACCOUNT_INFORMATION_VEHICLE_TURN_IN_DATE,
            value:
                details.vehicleTurnInDate === 'N/A'
                    ? 'N/A'
                    : formatDate(details.vehicleTurnInDate),
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_PAYOFF_DATE,
            value:
                details.accountStatus === 'AC'
                    ? 'N/A'
                    : formatDate(details.payoffDate),
        },
        {
            rowName: portfolioManagerDetails.ACCOUNT_INFORMATION_LEASE_TRANSFER,
            value: details.leaseTransfer,
        },
    ]);

    return (
        <div className='portfolio-detail-wrapper u-mt-large u-pl'>
            <div className='portfolio-detail-header'>
                <h5>{portfolioManagerDetails.HEADER_LABEL}</h5>
            </div>
            <PortfolioManagerDetailsItem
                title={
                    portfolioManagerDetails.CUSTOMER_INFORMATION_HEADER_LABEL
                }
                rows={customerInformationRows}
            />
            <PortfolioManagerDetailsItem
                title={portfolioManagerDetails.CONTACT_INFORMATION_HEADER_LABEL}
                rows={customerContactInformationRows}
            />
            <PortfolioManagerDetailsItem
                title={portfolioManagerDetails.VEHICLE_INFORMATION_HEADER_LABEL}
                rows={vehicleInformationRows}
            />
            <PortfolioManagerDetailsItem
                title={portfolioManagerDetails.ACCOUNT_INFORMATION_HEADER_LABEL}
                rows={accountInformationRows}
            />
        </div>
    );
};

const DetailsDrawer: React.FC<IPortfolioManagerDetailProps> = ({
    dealerId,
    accountNumber,
    country,
    setError,
    close,
}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [details, setDetails] = useState<IPortfolioManagerDetails>();

    useEffect(() => {
        setLoading(true);
        const service = new PortfolioService();
        service
            .getPortfolioManagerDetails(dealerId, accountNumber, country)
            .then((response) => {
                if (response?.data) {
                    setDetails(response.data);
                    setLoading(false);
                }
            })
            .catch(() => {
                setError();
                close();
            });
    }, [dealerId, accountNumber, country]);

    return (
        <Drawer onClose={close}>
            {loading || !details ? (
                loader
            ) : (
                <PortfolioManagerDetails details={details} />
            )}
        </Drawer>
    );
};

export default DetailsDrawer;
